// Contrail V3.4.0 — history bottom bar: playback controls + Speed graph + Data log.
// Graph: speed over time, cursor follows playback; click or drag to jump; zoom in/out on the time axis.
// Data log: every position Traccar stored for the period (with attributes), 50/100/200 per page; click a row to jump there.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { HistPoint, DataLog, LogRow } from "../../lib/history";
import { fetchDataLog, fijiClock, fijiDayClock } from "../../lib/history";
import { TZ } from "../../lib/format";
import type { Zone } from "../../lib/org";
import { zonesAt, zoneStack } from "../../lib/geo";

const SPEEDS = [1, 10, 30, 60, 120, 300];
const GAP_MS = 5 * 60_000;                 // gaps longer than this are drawn as "stopped" (0 km/h)
const STATUS_LABEL: Record<LogRow["status"], string> = { ok: "OK", unconfirmed: "Unconfirmed", no_fix: "No fix", duplicate: "Duplicate" };
const PREFERRED = ["ignition", "motion", "sat", "hdop", "power", "battery", "batteryLevel", "odometer", "totalDistance", "distance", "hours", "alarm", "status", "rssi"];

type Props = {
  deviceId: string; from: string; to: string;
  points: HistPoint[]; t0: number; t1: number;
  playT: number; setPlayT: (t: number) => void;
  playing: boolean; setPlaying: (p: boolean | ((x: boolean) => boolean)) => void;
  speed: number; setSpeed: (s: number) => void;
  curKmh: number; multiDay: boolean; zones: Zone[];
  onRow: (lng: number, lat: number) => void;
};

const fullTime = (ms: number | null) => (ms == null ? "—" : new Intl.DateTimeFormat("en-CA", {
  timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
}).format(new Date(ms)).replace(",", ""));

export default function HistoryBar(p: Props) {
  const [tab, setTab] = useState<"graph" | "log">("graph");
  const [open, setOpen] = useState(true);
  const clock = p.multiDay ? fijiDayClock : fijiClock;
  // publish the bar height on the map container (--pb-h) so the map credits / Google mark sit just above it
  const ref = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const el = ref.current, wrap = el?.closest(".mapwrap") as HTMLElement | null;
    if (!el || !wrap) return;
    const ro = new ResizeObserver(() => wrap.style.setProperty("--pb-h", `${el.offsetHeight + 12}px`));
    ro.observe(el);
    return () => { ro.disconnect(); wrap.style.removeProperty("--pb-h"); };
  }, []);
  return (
    <section ref={ref} className={`histbar ${open ? "" : "closed"}`} aria-label="History playback">
      <div className="hb-top">
        <div className="hb-tabs" role="tablist">
          <button role="tab" aria-selected={tab === "graph"} className={tab === "graph" ? "on" : ""} onClick={() => { setTab("graph"); setOpen(true); }}>Speed graph</button>
          <button role="tab" aria-selected={tab === "log"} className={tab === "log" ? "on" : ""} onClick={() => { setTab("log"); setOpen(true); }}>Data log</button>
        </div>
        <div className="hb-ctrl">
          <button className="hb-ic" title={p.playing ? "Pause" : "Play"} aria-label={p.playing ? "Pause" : "Play"}
            onClick={() => { if (p.playT >= p.t1) p.setPlayT(p.t0); p.setPlaying((x) => !x); }}>
            {p.playing ? <svg viewBox="0 0 16 16"><rect x="3" y="2" width="3.5" height="12" rx="1" /><rect x="9.5" y="2" width="3.5" height="12" rx="1" /></svg>
              : <svg viewBox="0 0 16 16"><path d="M4 2.5v11l9-5.5z" /></svg>}
          </button>
          <button className="hb-ic" title="Stop (back to start)" aria-label="Stop" onClick={() => { p.setPlaying(false); p.setPlayT(p.t0); }}>
            <svg viewBox="0 0 16 16"><rect x="3" y="3" width="10" height="10" rx="1.5" /></svg>
          </button>
          <select value={p.speed} onChange={(e) => p.setSpeed(Number(e.target.value))} aria-label="Playback speed">
            {SPEEDS.map((s) => <option key={s} value={s}>×{s}</option>)}
          </select>
          <input className="hb-range" type="range" min={p.t0} max={p.t1} step={1000} value={p.playT}
            onChange={(e) => { p.setPlaying(false); p.setPlayT(Number(e.target.value)); }} aria-label="Playback position" />
          <span className="mono hb-now"><b>{p.curKmh} km/h</b> · {clock(p.playT)}</span>
          <button className="hb-ic" onClick={() => setOpen(!open)} title={open ? "Hide graph / log" : "Show graph / log"} aria-label="Toggle panel">
            <svg viewBox="0 0 16 16" style={{ transform: open ? "rotate(180deg)" : undefined }}><path d="M3 10l5-5 5 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </div>
      </div>
      {open && tab === "graph" && <SpeedGraph {...p} />}
      {open && tab === "log" && <DataLogTable {...p} />}
    </section>
  );
}

// ---------------------------------------------------------------- speed graph
function SpeedGraph({ points, t0, t1, playT, setPlayT, setPlaying, multiDay }: Props) {
  const box = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(800);
  const [win, setWin] = useState<[number, number]>([t0, t1]);
  const [hover, setHover] = useState<{ x: number; t: number; kmh: number } | null>(null);
  useEffect(() => setWin([t0, t1]), [t0, t1]);
  useLayoutEffect(() => {
    const el = box.current; if (!el) return;
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el); setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  const H = 130, L = 60, R = 10, T = 8, B = 22;
  const iw = Math.max(50, w - L - R), ih = H - T - B;
  const [a, b] = win;
  const span = Math.max(1, b - a);
  const maxK = Math.max(40, Math.ceil(Math.max(0, ...points.map((p) => p[3])) / 20) * 20);
  const x = (t: number) => L + ((t - a) / span) * iw;
  const y = (k: number) => T + ih - (k / maxK) * ih;

  const path = useMemo(() => {
    if (!points.length) return "";
    const vis = points.filter((p) => p[2] >= a - GAP_MS && p[2] <= b + GAP_MS);
    if (!vis.length) return "";
    let d = `M${x(vis[0][2]).toFixed(1)},${y(0)}`;
    let prev: HistPoint | null = null;
    for (const p of vis) {
      if (prev && p[2] - prev[2] > GAP_MS) d += `L${x(prev[2]).toFixed(1)},${y(0)}L${x(p[2]).toFixed(1)},${y(0)}`;
      d += `L${x(p[2]).toFixed(1)},${y(p[3]).toFixed(1)}`;
      prev = p;
    }
    d += `L${x(prev![2]).toFixed(1)},${y(0)}Z`;
    return d;
  }, [points, a, b, w]); // eslint-disable-line

  // time ticks: pick a step that gives ~6–10 labels
  const ticks = useMemo(() => {
    const steps = [5, 10, 15, 30, 60, 120, 180, 360, 720, 1440].map((m) => m * 60_000);
    const step = steps.find((s) => span / s <= Math.max(4, Math.floor(iw / 90))) ?? steps[steps.length - 1];
    const out: number[] = [];
    const off = 12 * 3600_000; // Fiji UTC+12: align ticks to Fiji clock
    for (let t = Math.ceil((a + off) / step) * step - off; t <= b; t += step) out.push(t);
    return { out, step };
  }, [a, b, iw]); // eslint-disable-line
  const label = (t: number) => (ticks.step >= 1440 * 60_000 || (multiDay && fijiClock(t) === "00:00")) ? fijiDayClock(t).slice(0, 10) : fijiClock(t);

  const tAt = (clientX: number) => {
    const r = box.current!.getBoundingClientRect();
    return Math.min(b, Math.max(a, a + ((clientX - r.left - L) / iw) * span));
  };
  const nearest = (t: number) => {
    let best = points[0];
    for (const p of points) { if (Math.abs(p[2] - t) < Math.abs(best[2] - t)) best = p; if (p[2] > t) break; }
    return best;
  };
  const drag = useRef(false);
  const seek = (cx: number) => { setPlaying(false); setPlayT(tAt(cx)); };
  const zoom = (f: number) => {
    const c = playT >= a && playT <= b ? playT : (a + b) / 2;
    const ns = Math.min(t1 - t0, Math.max(5 * 60_000, span * f));
    let na = c - ns / 2, nb = c + ns / 2;
    if (na < t0) { nb += t0 - na; na = t0; }
    if (nb > t1) { na -= nb - t1; nb = t1; }
    setWin([Math.max(t0, na), Math.min(t1, nb)]);
  };

  return (
    <div className="hb-graph">
      <div className="hb-zoom">
        <button className="hb-ic" onClick={() => zoom(0.5)} title="Zoom in" aria-label="Zoom in"><svg viewBox="0 0 16 16"><circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M10.5 10.5 14 14M5 7h4M7 5v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg></button>
        <button className="hb-ic" onClick={() => zoom(2)} disabled={a <= t0 && b >= t1} title="Zoom out" aria-label="Zoom out"><svg viewBox="0 0 16 16"><circle cx="7" cy="7" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M10.5 10.5 14 14M5 7h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg></button>
        {(a > t0 || b < t1) && <button className="btn-ghost small" onClick={() => setWin([t0, t1])}>Whole period</button>}
      </div>
      <div className="hb-svgbox" ref={box}
        onPointerDown={(e) => { drag.current = true; (e.target as Element).setPointerCapture?.(e.pointerId); seek(e.clientX); }}
        onPointerMove={(e) => {
          const t = tAt(e.clientX); const n = points.length ? nearest(t) : null;
          setHover(n ? { x: x(t), t, kmh: n[3] } : null);
          if (drag.current) seek(e.clientX);
        }}
        onPointerUp={() => { drag.current = false; }} onPointerLeave={() => { setHover(null); drag.current = false; }}>
        <svg width={w} height={H} role="img" aria-label="Speed over time">
          {[0, .25, .5, .75, 1].map((f) => { const k = Math.round(maxK * f); return (
            <g key={f}><line x1={L} x2={L + iw} y1={y(k)} y2={y(k)} className="hb-grid" />
              {f > 0 && <text x={L - 6} y={y(k) + 4} textAnchor="end" className="hb-tick">{k} km/h</text>}</g>); })}
          {ticks.out.map((t) => <g key={t}><line x1={x(t)} x2={x(t)} y1={T} y2={T + ih} className="hb-grid" /><text x={x(t)} y={H - 6} textAnchor="middle" className="hb-tick">{label(t)}</text></g>)}
          <defs><clipPath id="hb-clip"><rect x={L} y={T} width={iw} height={ih} /></clipPath></defs>
          <path d={path} className="hb-area" clipPath="url(#hb-clip)" />
          {playT >= a && playT <= b && <line x1={x(playT)} x2={x(playT)} y1={T} y2={T + ih} className="hb-cursor" />}
          {hover && <g className="hb-hover"><line x1={hover.x} x2={hover.x} y1={T} y2={T + ih} />
            <text x={Math.min(hover.x + 6, L + iw - 120)} y={T + 12}>{hover.kmh} km/h · {fijiDayClock(hover.t)}</text></g>}
        </svg>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- data log
const cache = new Map<string, DataLog>();

function DataLogTable({ deviceId, from, to, setPlayT, setPlaying, zones, onRow, playT }: Props) {
  const key = `${deviceId}|${from}|${to}`;
  const [log, setLog] = useState<DataLog | null>(cache.get(key) ?? null);
  const [err, setErr] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(50);
  const [onlyIssues, setOnlyIssues] = useState(false);
  useEffect(() => {
    let live = true;
    if (cache.has(key)) { setLog(cache.get(key)!); return; }
    setLog(null); setErr(null);
    fetchDataLog(deviceId, from, to).then((d) => { cache.set(key, d); if (live) setLog(d); }).catch((e) => live && setErr(e.message));
    return () => { live = false; };
  }, [key]); // eslint-disable-line
  const rows = useMemo(() => (log?.rows ?? []).filter((r) => !onlyIssues || r.status !== "ok"), [log, onlyIssues]);
  const attrKeys = useMemo(() => {
    const seen = new Set<string>();
    for (const r of (log?.rows ?? []).slice(0, 2000)) for (const k of Object.keys(r.attrs)) seen.add(k);
    return [...PREFERRED.filter((k) => seen.has(k)), ...[...seen].filter((k) => !PREFERRED.includes(k)).sort()];
  }, [log]);
  const pages = Math.max(1, Math.ceil(rows.length / size));
  useEffect(() => setPage(0), [size, onlyIssues, key]);
  // follow playback: jump to the page holding the current time
  const curIdx = useMemo(() => { let i = 0; for (; i < rows.length; i++) if (rows[i].t > playT) break; return Math.max(0, i - 1); }, [rows, playT]);
  const shown = rows.slice(page * size, page * size + size);

  function exportCsv() {
    const head = ["time_fiji", "server_time_fiji", "latitude", "longitude", "altitude", "speed_kmh", "course", "status", "zone", ...attrKeys];
    const esc = (v: unknown) => { const s = v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const body = rows.map((r) => [fullTime(r.t), fullTime(r.server), r.lat, r.lng, r.alt, r.kmh, r.course, STATUS_LABEL[r.status], zoneStack(zonesAt(zones, r.lat, r.lng)), ...attrKeys.map((k) => r.attrs[k])].map(esc).join(","));
    const blob = new Blob(["﻿" + [head.join(","), ...body].join("\n")], { type: "text/csv;charset=utf-8" });
    const el = document.createElement("a");
    el.href = URL.createObjectURL(blob);
    el.download = `Contrail_Data-log_${from.slice(0, 10)}_${to.slice(0, 10)}.csv`;
    el.click(); URL.revokeObjectURL(el.href);
  }

  if (err) return <div className="hb-log"><p className="error pad">{err}</p></div>;
  if (!log) return <div className="hb-log"><p className="muted pad">Loading data log…</p></div>;
  return (
    <div className="hb-log">
      <div className="hb-log-scroll">
        <table className="hb-table">
          <thead><tr>
            <th>Time</th><th>Server time</th><th>Latitude</th><th>Longitude</th><th className="num">Altitude</th><th className="num">Speed</th><th className="num">Angle</th><th>Status</th><th>Zone</th>
            {attrKeys.map((k) => <th key={k}>{k}</th>)}
          </tr></thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={r.t + ":" + i} className={`${r.status !== "ok" ? "issue" : ""} ${page * size + i === curIdx ? "cur" : ""}`}
                onClick={() => { setPlaying(false); setPlayT(r.t); onRow(r.lng, r.lat); }}>
                <td className="mono">{fullTime(r.t)}</td>
                <td className="mono">{fullTime(r.server)}</td>
                <td className="mono">{r.lat.toFixed(6)}</td>
                <td className="mono">{r.lng.toFixed(6)}</td>
                <td className="num mono">{r.alt ?? "—"}</td>
                <td className="num mono">{r.kmh}</td>
                <td className="num mono">{r.course}</td>
                <td>{STATUS_LABEL[r.status]}</td>
                <td>{zoneStack(zonesAt(zones, r.lat, r.lng), 2)}</td>
                {attrKeys.map((k) => { const v = r.attrs[k]; return <td key={k} className="mono">{v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v)}</td>; })}
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={9 + attrKeys.length} className="muted">No positions{onlyIssues ? " with issues" : ""} in this period.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="hb-log-foot">
        <select value={size} onChange={(e) => setSize(Number(e.target.value))} aria-label="Rows per page">{[50, 100, 200].map((n) => <option key={n} value={n}>{n} / page</option>)}</select>
        <label className="check"><input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} /> Only issues</label>
        <span className="sub">{rows.length.toLocaleString()} positions{log.truncated ? ` (first ${log.rows.length.toLocaleString()} of ${log.total.toLocaleString()})` : ""}</span>
        <button className="btn-ghost small" onClick={() => setPage(Math.floor(curIdx / size))} title="Go to the page at the playback time">Go to cursor</button>
        <button className="btn-ghost small" onClick={exportCsv}>Export CSV</button>
        <span className="hb-pages">
          <button className="btn-ghost small" disabled={page === 0} onClick={() => setPage(page - 1)} aria-label="Previous page">‹</button>
          <span className="mono">{page + 1} / {pages}</span>
          <button className="btn-ghost small" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} aria-label="Next page">›</button>
        </span>
      </div>
    </div>
  );
}
